"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TripChain = exports.tripChainAttributesWithComposedAttributes = exports.tripChainAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const Trip_1 = require("./Trip");
const VisitedPlace_1 = require("./VisitedPlace");
const StartEndable_1 = require("./StartEndable");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.tripChainAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    'category',
    'isMultiLoop',
    'isConstrained',
    'mainActivity',
    'mainActivityCategory',
    'preData'
];
exports.tripChainAttributesWithComposedAttributes = [...exports.tripChainAttributes, 'trips', 'visitedPlaces'];
/**
 * A trip chain is a group or consecutive trips between two anchors
 * the anchors are usually home, work place or the main destination of
 * the trips group. A trip chain could be a closed (return to the same anchor)
 * or open (the end anchor is not the same as the start anchor)
 * trip chains can be unconstrained (like a shopping trip chain)
 * for which timing and/or location is usually flexible
 * or constrained (like a trip chain with the main anchor being a work or school place)
 * for which the timing and/or location is usually fixed/not flexible
 * TODO: document the official academic/students definition of the trip chain with more examples
 */
class TripChain extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, ['_trips', '_visitedPlaces', 'trips', 'visitedPlaces', '_journeyUuid']), exports.tripChainAttributes, exports.tripChainAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.trips = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._trips, Trip_1.Trip.unserialize, this._surveyObjectsRegistry);
        this.visitedPlaces = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._visitedPlaces, VisitedPlace_1.VisitedPlace.unserialize, this._surveyObjectsRegistry);
        this.journeyUuid = params._journeyUuid;
        this._surveyObjectsRegistry.registerTripChain(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get category() {
        return this._attributes.category;
    }
    set category(value) {
        this._attributes.category = value;
    }
    get isMultiLoop() {
        return this._attributes.isMultiLoop;
    }
    set isMultiLoop(value) {
        this._attributes.isMultiLoop = value;
    }
    get isConstrained() {
        return this._attributes.isConstrained;
    }
    set isConstrained(value) {
        this._attributes.isConstrained = value;
    }
    get mainActivity() {
        return this._attributes.mainActivity;
    }
    set mainActivity(value) {
        this._attributes.mainActivity = value;
    }
    get mainActivityCategory() {
        return this._attributes.mainActivityCategory;
    }
    set mainActivityCategory(value) {
        this._attributes.mainActivityCategory = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get trips() {
        return this._trips;
    }
    set trips(value) {
        this._trips = value;
    }
    get visitedPlaces() {
        return this._visitedPlaces;
    }
    set visitedPlaces(value) {
        this._visitedPlaces = value;
    }
    get journeyUuid() {
        return this._journeyUuid;
    }
    set journeyUuid(value) {
        this._journeyUuid = value;
    }
    get journey() {
        if (!this._journeyUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getJourney(this._journeyUuid);
    }
    get person() {
        var _a;
        return (_a = this.journey) === null || _a === void 0 ? void 0 : _a.person;
    }
    get household() {
        var _a, _b;
        return (_b = (_a = this.journey) === null || _a === void 0 ? void 0 : _a.person) === null || _b === void 0 ? void 0 : _b.household;
    }
    /**
     * Creates a TripChain object from sanitized parameters
     * @param {ExtendedTripChainAttributes | SerializedExtendedTripChainAttributes} params - Sanitized trip chain parameters
     * @returns {TripChain} New TripChain instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new TripChain(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = TripChain.validateParams(dirtyParams);
        const tripChain = errors.length === 0 ? new TripChain(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(tripChain);
    }
    static validateParams(dirtyParams, displayName = 'TripChain') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams, displayName));
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('category', dirtyParams.category, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isMultiLoop', dirtyParams.isMultiLoop, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isConstrained', dirtyParams.isConstrained, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('mainActivity', dirtyParams.mainActivity, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('mainActivityCategory', dirtyParams.mainActivityCategory, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const tripsAttributes = dirtyParams._trips !== undefined ? dirtyParams._trips : [];
        for (let i = 0, countI = tripsAttributes.length; i < countI; i++) {
            const tripAttributes = tripsAttributes[i];
            errors.push(...Trip_1.Trip.validateParams(tripAttributes, 'Trip'));
        }
        const visitedPlacesAttributes = dirtyParams._visitedPlaces !== undefined
            ? dirtyParams._visitedPlaces
            : [];
        for (let i = 0, countI = visitedPlacesAttributes.length; i < countI; i++) {
            const visitedPlaceAttributes = visitedPlacesAttributes[i];
            errors.push(...VisitedPlace_1.VisitedPlace.validateParams(visitedPlaceAttributes, 'VisitedPlace'));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_journeyUuid', dirtyParams._journeyUuid, displayName));
        return errors;
    }
}
exports.TripChain = TripChain;
TripChain._confidentialAttributes = ['preData'];
//# sourceMappingURL=TripChain.js.map